import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import {
  FiMoon, FiCheck, FiList, FiShield, FiZap, FiSliders,
  FiCommand, FiEyeOff, FiExternalLink,
} from 'react-icons/fi';
import Reel from '../components/Reel';
import ChromeStoreBtn from '../components/ChromeStoreBtn';
import { getProduct } from '../data/catalog';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="group border border-border bg-card p-6 transition-colors hover:border-signal/50">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-signal/10 transition-transform group-hover:scale-110">
      <SafeIcon icon={icon} className="h-6 w-6 text-signal" />
    </div>
    <h3 className="mb-2 font-display text-xl font-bold text-white">{title}</h3>
    <p className="text-sm leading-relaxed text-muted">{desc}</p>
  </div>
);

const SpecItem = ({ title, value }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border py-3">
    <span className="shrink-0 text-sm text-muted">{title}</span>
    <span className="text-right font-mono text-sm text-primary">{value}</span>
  </div>
);

const SmartMemorySaver = () => {
  const PRODUCT = getProduct('smart-memory-saver');

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="border-b border-border pb-16 pt-32">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-signal/20 bg-signal/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.16em] text-signal">
              <SafeIcon icon={FiMoon} /> Chrome extension
            </div>
            <h1 className="mb-6 font-display text-5xl font-extrabold tracking-tightest text-white md:text-7xl">
              Keep your tabs.<br />
              <span className="text-signal">Use less memory.</span>
            </h1>
            <p className="mx-auto mb-10 max-w-3xl text-xl leading-relaxed text-muted">
              Chrome slows to a crawl at forty tabs, and closing them means losing your place. Smart
              Memory Saver puts the idle ones to sleep and leaves them sitting in your tab strip.
              One click brings any of them back.
            </p>

            <div className="flex flex-col items-center gap-5">
              <ChromeStoreBtn accentColor="bg-signal hover:bg-signal/90" className="px-10 py-4 scale-110" href={PRODUCT.storeLink} />
              <span className="max-w-sm text-sm text-muted">
                <strong className="text-white">Free</strong>, with no ads and no upsell.
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reel */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — Smart Memory Saver</p>
          <Reel frames={PRODUCT.frames} label="Smart Memory Saver" sound={false} ratio="aspect-[16/10]" />
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-border bg-card/30 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold text-white">Careful about what it touches</h2>
            <p className="text-muted">The whole point is that nothing you were using goes quiet.</p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <FeatureCard
              icon={FiMoon}
              title="Only idle tabs"
              desc="Tabs you have not touched for your chosen time are discarded by Chrome, freeing their memory while the tab stays in the strip."
            />
            <FeatureCard
              icon={FiShield}
              title="Protected by default"
              desc="The tab you are on is never touched. Neither are pinned tabs, tabs playing audio, or video calls on Meet, Zoom, Teams and Webex."
            />
            <FeatureCard
              icon={FiSliders}
              title="Three intensities"
              desc="Manual sleeps nothing unless you ask. Balanced is the default. Aggressive halves the idle time. The timer runs from 5 minutes to 4 hours."
            />
            <FeatureCard
              icon={FiCommand}
              title="On demand"
              desc="Alt+Shift+S sleeps every idle tab, Alt+Shift+Z sleeps the one you are on, and the same actions sit in the right-click menu."
            />
          </div>
        </div>
      </section>

      {/* The honest bit */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-20 lg:flex-row">
            <div className="flex-1 space-y-8">
              <h2 className="font-display text-4xl font-bold leading-tight text-white">
                What sleeping actually does,<br />
                <span className="text-signal">including the part you should know.</span>
              </h2>
              <p className="text-muted">
                A sleeping tab is discarded by Chrome and reloads when you come back to it, so it
                behaves like a fresh page load. Anything you had typed but not saved is not preserved.
              </p>
              <p className="text-muted">
                That is exactly why the tab you are looking at is never touched, and why pinned tabs,
                audio, video calls and your own listed sites are protected before anything else. If a
                page matters, put it on the list and it will never be slept.
              </p>
              <div className="space-y-6">
                {[
                  [FiEyeOff, 'No network requests at all', 'The manifest sets connect-src to none, so the browser blocks outbound connections rather than us promising not to make them.'],
                  [FiZap, 'The memory figure is an estimate', 'Chrome does not report per-tab memory to extensions, so it is calculated at roughly 80 MB per sleeping tab and labelled as an estimate. The tab counts are exact.'],
                ].map(([icon, t, d]) => (
                  <div key={t} className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-signal/10 text-signal">
                      <SafeIcon icon={icon} />
                    </div>
                    <p className="text-muted">
                      <strong className="text-white">{t}:</strong> {d}
                    </p>
                  </div>
                ))}
              </div>
              <Link
                to="/privacy/smart-memory-saver"
                className="inline-flex items-center gap-2 font-medium text-white transition-colors hover:text-signal"
              >
                Read the privacy policy <SafeIcon icon={FiExternalLink} />
              </Link>
            </div>

            <div className="w-full max-w-md flex-1">
              <div className="border border-border bg-card p-8">
                <h3 className="mb-6 flex items-center gap-2 font-display text-xl font-bold text-white">
                  <SafeIcon icon={FiList} className="text-signal" /> Tech Specs
                </h3>
                <div className="space-y-1">
                  <SpecItem title="Platform" value="Chrome 110+" />
                  <SpecItem title="Manifest" value="MV3" />
                  <SpecItem title="Permissions" value="tabs, storage, alarms, contextMenus" />
                  <SpecItem title="Host permissions" value="None" />
                  <SpecItem title="Network requests" value="None — blocked by CSP" />
                  <SpecItem title="Package size" value="Under 200 KB" />
                </div>
                <div className="mt-8 border-t border-border pt-6">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="font-bold text-white">Price</span>
                    <span className="font-mono text-xl font-bold text-signal">{PRODUCT.price}</span>
                  </div>
                  <p className="text-xs text-muted">
                    Available now on the Chrome Web Store.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SmartMemorySaver;
